import { LOCATION_TYPES, ATTENDEE_TYPE } from '../constants.mjs';

function extract_political_addresses(location, by_id_hash) {
	const coords = location.co;
	let parent = null;
	let key;
	let next_key;
	let top;
	coords.p8 = null;
	coords.p9 = null;
	for (let ii = 8, len = 1; ii > len; --ii) {
		key = 'p' + ii;
		next_key = 'p' + (ii - 1);
		if (coords[key] && !coords[next_key] && ii > 1) {
			coords[next_key] = coords[key];
		}
	}
	for (let ii = 1, len = 8; ii < len; ++ii) {
		key = 'p' + ii;
		next_key = 'p' + (ii + 1);
		if (coords[key]) {
			let tree_key = coords[key];
			if (parent && parent.id !== coords[key]) {
				tree_key = parent.path + coords[key];
			}
			if (!by_id_hash[tree_key]) {
				by_id_hash[tree_key] = {
					id: tree_key,
					text: coords[key],
					locationtype: 2,
					items: [],
					path: tree_key,
				};
			}
			if (parent && parent.items.indexOf(by_id_hash[tree_key]) === -1) {
				parent.items.push(by_id_hash[tree_key]);
			}
			if (!parent) {
				top = key;
			}
			if (coords[key] !== coords[next_key]) {
				parent = by_id_hash[tree_key];
			}
		}
	}
	parent.items.push(by_id_hash[location.id]);
	return by_id_hash[coords[top]];
}

export function createHierarchyTree(locations) {
	const tree = [];
	const by_id = {};
	const processed_before_parent = {};
	const top_level = [];
	for (let ii = 0, len = locations.length; ii < len; ++ii) {
		const location = locations[ii];
		by_id[location.id] = {
			id: location.id,
			items: processed_before_parent[location.id] ?? [],
			text: location.name,
			rt: location._resourceTypes?.slice() || [],
			address: location.address,
		};
		if (location.locationtype === 2) {
			by_id[location.id].locationtype = 2;
		}
		delete processed_before_parent[location.id];
		if (location.pids.length) {
			// it has parents
			//for hierarchy we only care about the immediate parent
			const immediate_parent_key = location.pids[0];
			if (by_id[immediate_parent_key]) {
				by_id[immediate_parent_key].items.push(by_id[location.id]);
			} else {
				if (Array.isArray(processed_before_parent[immediate_parent_key])) {
					processed_before_parent[immediate_parent_key].push(by_id[location.id]);
				} else {
					processed_before_parent[immediate_parent_key] = [by_id[location.id]];
				}
			}
		} else {
			//top level node compute geo hierarchy
			if (location.co) {
				const path = extract_political_addresses(location, by_id);
				if (top_level.indexOf(path.id) === -1) {
					// if we don't already have the top level add it
					top_level.push(path.id);
				}
			}
		}
	}
	for (let ii = 0, len = top_level.length; ii < len; ++ii) {
		const id = top_level[ii];
		tree.push(by_id[id]);
	}
	return tree;
}

export function validateFilters(location, filters, searchValue, named_filter_ids) {
	let valid = true;
	if (location.locationtype === LOCATION_TYPES.LOCATION) {
		if (searchValue) {
			valid = false;
			searchValue = searchValue.replace(/[,;\s]+/g, ' ').toLowerCase().split(' ').filter(s => s);
			const lowercased_location_name = location.name.trim().toLowerCase();
			if (searchValue.some(value => (lowercased_location_name.indexOf(value) > -1))) {
				valid = true;
			}
		}
		if (valid) {
			for (let fieldid in filters) {
				if (Array.isArray(filters[fieldid])) {
					if (filters[fieldid].length) {
						if (fieldid === named_filter_ids['resourcetypefilter']) {
							if (valid && !filters[fieldid].some(resource_id => {
								return location._resourceTypes.indexOf(+resource_id) !== -1;
							})) {
								valid = false;
							}
						} else if (fieldid === named_filter_ids['locationImages']) {
							//ignore it
						} else if (fieldid === named_filter_ids['locationfilter']) {
							if (filters[fieldid].indexOf(location.id) === -1) {
								valid = false;
							}
						} else {
							if (!location.fieldsValues[fieldid] || !location.fieldsValues[fieldid].length || filters[fieldid].every(val => location.fieldsValues[fieldid].indexOf(+val) === -1)) {
								valid = false;
							}
						}
					}
				} else if (filters[fieldid] !== undefined && filters[fieldid] !== null && filters[fieldid] !== location.fieldsValues[fieldid]) {
					if (fieldid === named_filter_ids['seatingCapacity']) {
						if (filters[fieldid] > (location.fieldsValues[fieldid] ?? 0)) {
							valid = false;
						}
					} else if (typeof filters[fieldid] === 'number') {
						if (filters[fieldid] > (location.fieldsValues[fieldid] ?? 0)) {
							valid = false;
						}
					} else if (typeof filters[fieldid] === 'boolean') {
						if (Boolean(filters[fieldid]) && Boolean(filters[fieldid]) !== Boolean(location.fieldsValues[fieldid])) {
							valid = false;
						}
					} else if (filters[fieldid] === '') {
						// ignore empty filter case
					} else {
						valid = false;
					}
				}
			}
		}
	} else {
		valid = false;
	}
	return valid;
}

export function calculateDistanceWorldCoords(fromlat, fromlng, tolat, tolng) {
	const pk = 180 / Math.PI;
	const a1 = fromlat / pk;
	const a2 = fromlng / pk;
	const b1 = tolat / pk;
	const b2 = tolng / pk;

	const t1 = Math.cos(a1) * Math.cos(a2) * Math.cos(b1) * Math.cos(b2);
	const t2 = Math.cos(a1) * Math.sin(a2) * Math.cos(b1) * Math.sin(b2);
	const t3 = Math.sin(a1) * Math.sin(b1);

	let test = 6366000 * Math.acos(t1 + t2 + t3);

	if (test > 9999999) {
		test = 9999999;
	}

	const tt = parseFloat(test.toFixed(3));

	return tt;
}

export function convertDateToASPNET(date, ignoreTimeZone) {
	var x = date;
	var y = null;
	if ((x + '').indexOf('/Date(') === 0) {
		return x;
	} else if (!x || x === '' || isDateTimeMinValue(x)) {
		y = '0';
	} else {
		if (typeof x === 'string' && x !== '') {
			y = new Date(x);
			y = y.valueOf() - (ignoreTimeZone === true ? 0 : y.getTimezoneOffset() * 60000);
		} else {
			y = x.valueOf() - (ignoreTimeZone === true ? 0 : x.getTimezoneOffset() * 60000);
		}
	}
	return '/Date(' + y + ')/';
}

export function convertDateToISOString(date, ignoreTimeZone) {
	if (!date) {
		return null;
	} else if (typeof date === "string") {
		const parsedDate = Date.parse(date);
		if (!isNaN(parsedDate)) {
			const offset = ignoreTimeZone === true ? 0 : new Date(parsedDate).getTimezoneOffset() * 60000;
			return new Date(parsedDate + offset).toISOString();
		}
	} else if (date instanceof Date) {
		return date.toISOString();
	}
	return null;
}

export function isDateTimeMinValue(date) {
	if (typeof date !== 'undefined') {
		var x = null;
		if (typeof date === 'string' && date !== '') {
			try {
				x = new Date(date);
			} catch (e) {
				console.error(e);
			}
		} else {
			x = date;
		}
		if (x && typeof x.getMonth !== 'undefined') {
			if (x.getTime() > new Date(1970, 1, 3).getTime()) {
				return false;
			}
		}
	}
	return true;
}

export function convertISOtoDate(datestr, ignoreTimeZone = false) {
	if (datestr === 0) {
		return 0;
	} else if (typeof datestr !== 'string') {
		return datestr; // only string is valid
	}
	const extractedDate = new Date(Date.parse(datestr));
	return new Date(extractedDate.getTime() + ((ignoreTimeZone ? 0 : extractedDate.getTimezoneOffset()) * 60000));
}

export function getSerializedAttendeesByType(attendees, type) {
	return attendees
		.filter(attendee => attendee.attendeetype === type)
		.map(attendee => {
			const attendeeData = {
				email: attendee.user?.email,
				physicallocation: attendee.physicallocation,
				collabgroups: attendee.collabgroups,
				directassign: attendee.directassign,
				userid: attendee.userid,
			};
			if (attendee.locationid) {
				attendeeData.locationid = attendee.locationid;
			}
			// add attendeetype to host
			if (type === ATTENDEE_TYPE.HOST) {
				attendeeData.attendeetype = attendee.attendeetype;
			}
			return attendeeData;
		});
}
export function process_fast_locations_for_se(all_location_groups, all_locations = [], options = { settings: false }) {
	function get_field_value(f) {
		//null when we don't get any of them to be defaulted based on data type later when it's known
		if (f.a) {
			return [f.a];
		} else {
			return f.t || f.n || (f.d && new Date(Date.parse(f.d))) || f.g || null;
		}
	}
	return all_locations.map(l => {
		const transformed_location = {
			id: l.id,
			name: l.n,
			locationtype: l.t,
			get fieldsValues() {
				return l.f?.reduce((acc, f) => {
					if (acc[f.id] !== undefined && acc[f.id] !== null) {
						const field_value = get_field_value(f);
						if (Array.isArray(acc[f.id])) {
							if (Array.isArray(field_value)) {
								acc[f.id].push.apply(acc[f.id], field_value);
							} else {
								acc[f.id].push(field_value);
							}
						} else {
							if (Array.isArray(field_value)) {
								acc[f.id].push.apply(acc[f.id], field_value);
							} else {
								acc[f.id] = [acc[f.id], get_field_value(f)];
							}
						}
					} else {
						acc[f.id] = get_field_value(f);
					}
					return acc;
				}, {}) ?? {};
			},
			groups: l.gid,
			co: l.co?.[0] ?? null,
			order: l.o,
			resourcetypes: l.rt,
			_resourceTypes: l.rt,
			pids: l.pid.reverse(),
			get address() {
				return this._calculated_add
					|| this.co?.a
					|| (this.pids.length && (this._calculated_add = all_location_groups
						.filter(l => l.co && this.pids.indexOf(l.id) > -1)
						.sort((a, b) => {
							if (this.pids.indexOf(a) < this.pids.indexOf(b)) {
								return -1;
							} else {
								return 1;
							}
						})[0].co?.[0].a));
			},
		};

		if (options.settings) {
			transformed_location.settings = l.s?.map(s => {
				return {
					id: s.n,
					value: s.v
				};
			}) ?? [];
		}

		return transformed_location;
	});
}

export function process_fast_locations(all_location_groups, all_locations) {
	function get_field_value(f) {
		//null when we don't get any of them to be defaulted based on data type later when it's known
		if (f.a) {
			return [f.a];
		} else {
			return f.t || f.n || (f.d && new Date(Date.parse(f.d))) || f.g || null;
		}
	}
	return (all_locations.view ?? []).map(l => {
		const transformed_location = {
		};
		Object.defineProperties(transformed_location, {
			locationVM: {
				value: true,
			},
			id: {
				enumerable: true,
				value: l.id,
			},
			name: {
				enumerable: true,
				value: l.n,
			},
			locationtype: {
				enumerable: false,
				value: l.t,
			},
			displayorder: {
				enumerable: false,
				value: l.o,
			},
			preparationTime: {
				enumerable: false,
				value: l.p,
			},
			floorplanid: {
				enumerable: false,
				value: l.fpid,
			},
			cleanupTime: {
				enumerable: false,
				value: l.c,
			},
			leadTime: {
				enumerable: false,
				value: l.l,
			},
			cleanupLead: {
				enumerable: false,
				value: l.cl,
			},
			category: {
				enumerable: false,
				value: l.ca,
			},
			fieldsValues: {
				enumerable: false,
				value: l.f?.reduce((acc, f) => {
					if (acc[f.id] !== undefined && acc[f.id] !== null) {
						const field_value = get_field_value(f);
						if (Array.isArray(acc[f.id])) {
							if (Array.isArray(field_value)) {
								acc[f.id].push.apply(acc[f.id], field_value);
							} else {
								acc[f.id].push(field_value);
							}
						} else {
							if (Array.isArray(field_value)) {
								acc[f.id].push.apply(acc[f.id], field_value);
							} else {
								acc[f.id] = [acc[f.id], get_field_value(f)];
							}
						}
					} else {
						acc[f.id] = get_field_value(f);
					}
					return acc;
				}, {}) ?? {},
			},
			services: {
				enumerable: false,
				value: l.sid,
			},
			groups: {
				enumerable: false,
				value: l.gid,
			},
			settings: {
				enumerable: false,
				configurable: true,
				value: l.s?.map(s => {
					return {
						id: s.n,
						value: s.v
					};
				}) ?? [],
			},
			co: {
				enumerable: false,
				value: l.co?.[0] ?? null,
			},
			calculated_address: {
				enumerable: false,
				get() {
					return this.address;
				},
			},
			calculated_lat: {
				enumerable: false,
				get() {
					return this.latitude;
				},
			},
			calculated_lng: {
				enumerable: false,
				get() {
					return this.longitude;
				},
			},
			subfloorplan: {
				enumerable: false,
				get() {
					return Boolean(l.sbfp);
				},
			},
			childhasfloorplan: {
				enumerable: false,
				get() {
					return false;
					// Boolean(sw2.settingsService._all_locations
					// 	.filter(l => l.floorplanid && l.pids.indexOf(this.id) > -1)
					// 	.sort((a, b) => {
					// 		if (this.pids.indexOf(a) < this.pids.indexOf(b)) {
					// 			return -1;
					// 		} else {
					// 			return 1;
					// 		}
					// 	})[0]);
				},
			},
			floor: {
				enumerable: false,
				get() {
					return l.fl;
				}
			},
			directfloorplan: {
				enumerable: false,
				get() {
					return Boolean(this.floorplanid);
				}
			},
			_resourceTypes: {
				enumerable: false,
				value: l.rt,
			},
			pids: {
				enumerable: false,
				value: l.pid.reverse(),
			},
			longitude: {
				enumerable: false,
				get() {
					return this._calculated_lng
						|| this.co?.lng
						|| (this._calculated_lng = all_location_groups
							.filter(l => l.co && this.pids.indexOf(l.id) > -1)
							.sort((a, b) => {
								if (this.pids.indexOf(a) < this.pids.indexOf(b)) {
									return -1;
								} else {
									return 1;
								}
							})[0].co?.[0].lng);
				}
			},
			latitude: {
				enumerable: false,
				get() {
					return this._calculated_lat
						|| this.co?.lat
						|| (this._calculated_lat = all_location_groups
							.filter(l => l.co && this.pids.indexOf(l.id) > -1)
							.sort((a, b) => {
								if (this.pids.indexOf(a) < this.pids.indexOf(b)) {
									return -1;
								} else {
									return 1;
								}
							})[0].co?.[0].lat);
				}
			},
			address: {
				enumerable: true,
				get() {
					return this._calculated_add
						|| this.co?.a
						|| (this.pids.length && (this._calculated_add = all_location_groups
							.filter(l => l.co && this.pids.indexOf(l.id) > -1)
							.sort((a, b) => {
								if (this.pids.indexOf(a) < this.pids.indexOf(b)) {
									return -1;
								} else {
									return 1;
								}
							})[0].co?.[0].a));
				}
			},
			_calculated_add: {
				enumerable: false,
				writable: true,
			},
			_calculated_lat: {
				enumerable: false,
				writable: true,
			},
			_calculated_lng: {
				enumerable: false,
				writable: true,
			},
			ancestorhasfloorplan: {
				enumerable: false,
				get() {
					return Boolean(this.pids.length) && all_location_groups
						.some(l => l.fpid && this.pids.indexOf(l.id) > -1);
				}
			},
			relatedfloorplan: {
				enumerable: false,
				get() {
					return this.childhasfloorplan || this.ancestorhasfloorplan;
				}
			},
			notAvailable: {
				enumerable: false,
				value: function () {
					return window.locationVM.fn.notAvailable.call(this);
				}
			},
			getBlockingReservation: {
				enumerable: false,
				value: function () {
					return window.locationVM.fn.getBlockingReservation.call(this);
				}
			},
			nextAvailableDate: {
				enumerable: false,
				value: function () {
					return window.locationVM.fn.nextAvailableDate.call(this);
				}
			},
			availableNowWithoutRestriction: {
				enumerable: false,
				value: function () {
					return window.locationVM.fn.availableNowWithoutRestriction.call(this);
				}
			},
			hasAvailabilityData: {
				enumerable: false,
				value: function () {
					return window.locationVM.fn.hasAvailabilityData.call(this);
				}
			},
			nextAvailable: {
				enumerable: false,
				value: function () {
					return window.locationVM.fn.nextAvailable.call(this);
				}
			},
			availableLaterWithoutRestriction: {
				enumerable: false,
				value: function () {
					return window.locationVM.fn.availableLaterWithoutRestriction.call(this);
				}
			},
			matchesString: {
				enumerable: true,
				value: function () {
					return window.locationVM.fn.matchesString.call(this);
				}
			},
			servicesString: {
				enumerable: true,
				value: function () {
					return window.locationVM.fn.servicesString.call(this);
				}
			},
			uid: {
				enumerable: false,
				value: kendo.guid()
			},
			matches: {
				enumerable: true,
				value: null
			},
			distance: {
				enumerable: true,
				value: null
			},
			get: {
				enumerable: false,
				value: function (prop) {
					// console.log('oldgetter', prop);
					return this[prop];
				}
			},
			set: {
				enumerable: false,
				value: function (prop, val) {
					// console.log('oldsetter', prop);
					return this[prop] = val;
				}
			},
		});
		return transformed_location;
	});
}

export function makeCDNUrl(url, domain, useCDNFlag = {}) {
	const useCDN = useCDNFlag[`/${url}`] ?? false;
	const cdnURL = new URL(domain);
	const tlds = ['self', 'sw2test', 'smartway2book', 'smartway2preview'];
	const hostParts = cdnURL.host.split('.');
	if (useCDN) {
		if (typeof useCDN === 'string' && useCDN.indexOf('https://') === 0) {
			try {
				const useCDNUrl = new URL(useCDN);
				cdnURL.host = useCDNUrl.host;
			} catch (error) {
				console.warn('Invalid setting for FF-use-cdn: ', useCDN);
			}
		} else if (tlds.indexOf(hostParts[0]) === -1) {
			// if the first subdomain is not one of the known ones inject .cdn in the locations url
			hostParts.splice(1, 0, 'cdn');
			cdnURL.host = hostParts.join('.');
		}
		cdnURL.pathname = url;
		return cdnURL.toString();
	} else {
		return url;
	}
}
